import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Row, Col, Modal, Form, Button, Table, Alert } from 'react-bootstrap';
import * as XLSX from 'xlsx';
import './HR.css';

export default function HRDashboard() {
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  const headers = { headers: { Authorised: token } };

  const [locations, setLocations]         = useState([]);
  const [employees, setEmployees]         = useState([]);
  const [todayData, setTodayData]         = useState([]);
  const [selectedLocation, setSelectedLoc] = useState('');
  const [reportEmployee, setReportEmployee] = useState('');
  const [reportMonth, setReportMonth]     = useState(new Date().toISOString().slice(0,7));
  const [reportData, setReportData]       = useState([]);
  const [showReport, setShowReport]       = useState(false);
  const [loading, setLoading]             = useState(false);
  const [lastRefresh, setLastRefresh]     = useState(new Date());
  
  // Import Excel
  const [showImportModal, setShowImportModal] = useState(false);
  const [importData, setImportData]           = useState([]);
  const [importLoading, setImportLoading]     = useState(false);
  const [importResult, setImportResult]       = useState(null);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [locsRes, empsRes, todayRes] = await Promise.all([
        axios.get('/api/hr/locations', headers),
        axios.get(`/api/hr/employees${selectedLocation ? `?locationId=${selectedLocation}` : ''}`, headers),
        axios.get(`/api/hr/pointage/today${selectedLocation ? `?locationId=${selectedLocation}` : ''}`, headers)
      ]);
      setLocations(locsRes.data);
      setEmployees(empsRes.data);
      setTodayData(todayRes.data);
      setLastRefresh(new Date());
    } catch { /* silent */ } finally {
      setLoading(false);
    }
  }, [selectedLocation]);

  useEffect(() => { fetchAll(); }, [fetchAll]);
  useEffect(() => {
    const interval = setInterval(fetchAll, 120000); // refresh toutes les 2 min
    return () => clearInterval(interval);
  }, [fetchAll]);

  const fetchReport = async () => {
    try {
      const start = `${reportMonth}-01`;
      const lastDay = new Date(reportMonth.split('-')[0], reportMonth.split('-')[1], 0).getDate();
      const end = `${reportMonth}-${String(lastDay).padStart(2,'0')}`;
      const { data } = await axios.get(
        `/api/hr/pointage/report?${reportEmployee ? `employeeId=${reportEmployee}&` : ''}startDate=${start}&endDate=${end}`,
        headers
      );
      setReportData(data);
      setShowReport(true);
    } catch { alert('Erreur chargement rapport'); }
  };

  const handleFileImport = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (evt) => {
      const bstr = evt.target.result;
      const wb = XLSX.read(bstr, { type: 'binary' });
      const wsname = wb.SheetNames[0];
      const ws = wb.Sheets[wsname];
      const data = XLSX.utils.sheet_to_json(ws);
      
      // Normaliser les clés (au cas où elles seraient différentes dans l'excel)
      const normalized = data.map(row => ({
        name: row.Nom || row.Name || row.name || '',
        email: row.Email || row.email || '',
        matricule: row.Matricule || row.matricule || row.ID || '',
        role: row.Role || row.role || 'user',
        position: row.Fonction || row.fonction || ''

      })).filter(r => r.name && r.matricule);

      setImportData(normalized);
      setShowImportModal(true);
    };
    reader.readAsBinaryString(file);
  };

  const saveImport = async () => {
    setImportLoading(true);
    setImportResult(null);
    try {
      const res = await axios.post('/api/auth/BulkImport', { employees: importData }, headers);
      setImportResult(res.data);
      fetchAll(); // Rafraîchir la liste
    } catch (err) {
      alert('Erreur lors de l\'importation');
    } finally {
      setImportLoading(false);
    }
  };

  const formatTime = d => d ? new Date(d).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : '—';
  const formatMinutes = min => {
    const h = Math.floor(min / 60);
    const m = min % 60;
    return h > 0 ? `${h}h${String(m).padStart(2,'0')}` : `${m}min`;
  };

  // Stats globales
  const totalPresents = todayData.filter(e => e.status === 'present' || e.status === 'retard').length;
  const totalRetards  = todayData.filter(e => e.status === 'retard').length;
  const totalAbsents  = todayData.filter(e => e.status === 'absent').length;
  const currentlyIn   = todayData.filter(e => {
    const pts = e.pointages || [];
    let lastIn = false;
    pts.forEach(p => { if (p.type === 'entree') lastIn = true; if (p.type === 'sortie') lastIn = false; });
    return lastIn;
  }).length;

  // Pour le rapport — grouper par employé
  const reportByEmployee = {};
  reportData.forEach(r => {
    const id = r.employee?._id || r.employee;
    if (!reportByEmployee[id]) reportByEmployee[id] = { employee: r.employee, days: [] };
    reportByEmployee[id].days.push(r);
  });

  return (
    <div className="hr-page">
      <div className="hr-header">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h1>🏢 Tableau de Bord RH</h1>
            <p>Vue en temps réel — Actualisé à {lastRefresh.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}</p>
          </div>
          <div>
            <button className="hr-btn hr-btn-primary" onClick={() => navigate('/HR/salary')}>
              💰 Gestion des Salaires
            </button>
          </div>
        </div>
      </div>

      {/* Filtre localisation */}
      <div className="hr-card" style={{ marginBottom: 24 }}>
        <Row className="align-items-end g-3">
          <Col md={4}>
            <div className="hr-form-group" style={{ marginBottom: 0 }}>
              <label>Filtrer par Localisation</label>
              <select className="hr-form-control" value={selectedLocation} onChange={e => setSelectedLoc(e.target.value)}>
                <option value="">Tous les sites</option>
                {locations.map(l => <option key={l._id} value={l._id}>{l.name}</option>)}
              </select>
            </div>
          </Col>
          <Col md={4}>
            <button className="hr-btn hr-btn-primary" onClick={fetchAll} disabled={loading} style={{ width: '100%' }}>
              {loading ? '⏳ Chargement...' : '🔄 Actualiser'}
            </button>
          </Col>
          <Col md={4}>
            <div className="import-excel-wrap">
              <label htmlFor="excel-upload" className="hr-btn hr-btn-success" style={{ width: '100%', textAlign: 'center', cursor: 'pointer', display: 'block' }}>
                📊 Importer Employés (Excel)
              </label>
              <input 
                id="excel-upload" 
                type="file" 
                accept=".xlsx,.xls" 
                style={{ display: 'none' }} 
                onChange={handleFileImport} 
              />
            </div>
          </Col>
        </Row>
      </div>

      {/* KPIs du jour */}
      <div className="hr-stat-grid">
        <div className="hr-stat-card success">
          <div className="stat-label">Présents Aujourd'hui</div>
          <div className="stat-value">{totalPresents}</div>
          <div className="stat-sub">sur {employees.length} employés</div>
        </div>
        <div className="hr-stat-card info">
          <div className="stat-label">Actuellement en Poste</div>
          <div className="stat-value">{currentlyIn}</div>
          <div className="stat-sub">pointage entrée actif</div>
        </div>
        <div className="hr-stat-card warning">
          <div className="stat-label">Retards</div>
          <div className="stat-value">{totalRetards}</div>
          <div className="stat-sub">arrivées tardives</div>
        </div>
        <div className="hr-stat-card danger">
          <div className="stat-label">Absences</div>
          <div className="stat-value">{totalAbsents}</div>
          <div className="stat-sub">non pointés</div>
        </div>
      </div>

      {/* Tableau temps réel */}
      <div className="hr-card" style={{ marginBottom: 24 }}>
        <div className="hr-card-title"><span className="icon">📋</span> Pointages Aujourd'hui</div>
        <div style={{ overflowX: 'auto' }}>
          <table className="schedule-table">
            <thead>
              <tr>
                <th>Employé</th>
                <th>Localisation</th>
                <th>1ère Entrée</th>
                <th>Dernière Sortie</th>
                <th>Temps Travaillé</th>
                <th>Statut</th>
                <th>Retard</th>
              </tr>
            </thead>
            <tbody>
              {todayData.length === 0 ? (
                <tr><td colSpan={7} style={{ textAlign: 'center', color: '#a0aec0', padding: 30 }}>
                  {loading ? '⏳ Chargement...' : 'Aucun pointage enregistré aujourd\'hui'}
                </td></tr>
              ) : todayData.map((emp, i) => {
                const firstIn = emp.pointages?.find(p => p.type === 'entree');
                const lastOut = [...(emp.pointages || [])].reverse().find(p => p.type === 'sortie');
                return (
                  <tr key={i}>
                    <td>
                      <div className="employee-row">
                        <div className="employee-avatar">{emp.employee?.name?.charAt(0)?.toUpperCase()}</div>
                        <div>
                          <div className="employee-name">{emp.employee?.name}</div>
                          <div className="employee-role">{emp.employee?.matricule}</div>
                        </div>
                      </div>
                    </td>
                    <td>{emp.pointages?.[0]?.location?.name || '—'}</td>
                    <td><strong>{firstIn ? formatTime(firstIn.timestamp) : '—'}</strong></td>
                    <td>{lastOut ? formatTime(lastOut.timestamp) : '—'}</td>
                    <td><strong style={{ color: '#4318FF' }}>{emp.totalMinutes > 0 ? formatMinutes(emp.totalMinutes) : '—'}</strong></td>
                    <td>
                      <span className={`status-badge ${emp.status}`}>
                        {emp.status === 'present' ? '✅ Présent' : emp.status === 'retard' ? '⚠ Retard' : '❌ Absent'}
                      </span>
                    </td>
                    <td>
                      {firstIn?.delayMinutes > 0
                        ? <span style={{ color: '#ffb547', fontWeight: 700 }}>{firstIn.delayMinutes} min</span>
                        : <span style={{ color: '#a0aec0' }}>—</span>
                      }
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Générateur rapport mensuel */}
      <div className="hr-card">
        <div className="hr-card-title"><span className="icon">📊</span> Rapport Mensuel des Heures</div>
        <Row className="align-items-end g-3">
          <Col md={3}>
            <div className="hr-form-group" style={{ marginBottom: 0 }}>
              <label>Employé (optionnel)</label>
              <select className="hr-form-control" value={reportEmployee} onChange={e => setReportEmployee(e.target.value)}>
                <option value="">Tous les employés</option>
                {employees.map(e => <option key={e._id} value={e._id}>{e.name}</option>)}
              </select>
            </div>
          </Col>
          <Col md={3}>
            <div className="hr-form-group" style={{ marginBottom: 0 }}>
              <label>Mois</label>
              <input type="month" className="hr-form-control" value={reportMonth} onChange={e => setReportMonth(e.target.value)} />
            </div>
          </Col>
          <Col md={3}>
            <button className="hr-btn hr-btn-primary" onClick={fetchReport} style={{ width: '100%' }}>
              📈 Générer Rapport
            </button>
          </Col>
        </Row>
      </div>

      {/* Modal Rapport Mensuel */}
      <Modal show={showReport} onHide={() => setShowReport(false)} size="xl" centered>
        <Modal.Header closeButton style={{ background: 'linear-gradient(135deg,#4318FF,#6b46ff)', color: 'white' }}>
          <Modal.Title>📊 Rapport des Heures — {reportMonth}</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: 28 }}>
          {Object.keys(reportByEmployee).length === 0 ? (
            <p className="text-center text-muted py-4">Aucune donnée pour cette période</p>
          ) : Object.values(reportByEmployee).map(({ employee: emp, days }, idx) => {
            const totalMins   = days.reduce((s, d) => s + (d.minutesTravaillees || 0), 0);
            const presents    = days.filter(d => d.status !== 'absent').length;
            const retards     = days.filter(d => d.status === 'retard').length;
            const absents     = days.filter(d => d.status === 'absent').length;
            return (
              <div key={idx} style={{ marginBottom: 32 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 16 }}>
                  <div className="employee-avatar" style={{ width: 48, height: 48, fontSize: '1.1rem' }}>{emp?.name?.charAt(0)?.toUpperCase()}</div>
                  <div>
                    <div style={{ fontWeight: 800, fontSize: '1.1rem', color: '#0f1535' }}>{emp?.name || emp}</div>
                    <div style={{ color: '#718096', fontSize: '0.82rem' }}>{emp?.position || emp?.department} — {emp?.matricule}</div>

                  </div>
                </div>
                <div className="hr-stat-grid" style={{ marginBottom: 16 }}>
                  <div className="hr-stat-card success"><div className="stat-label">Jours Présents</div><div className="stat-value">{presents}</div></div>
                  <div className="hr-stat-card warning"><div className="stat-label">Retards</div><div className="stat-value">{retards}</div></div>
                  <div className="hr-stat-card danger"><div className="stat-label">Absences</div><div className="stat-value">{absents}</div></div>
                  <div className="hr-stat-card"><div className="stat-label">Total Heures</div><div className="stat-value" style={{ fontSize: '1.4rem' }}>{formatMinutes(totalMins)}</div></div>
                </div>
                <div style={{ overflowX: 'auto' }}>
                  <table className="schedule-table">
                    <thead>
                      <tr><th>Date</th><th>Entrée</th><th>Sortie</th><th>Durée</th><th>Statut</th><th>Retard</th></tr>
                    </thead>
                    <tbody>
                      {days.map((d, i) => (
                        <tr key={i}>
                          <td>{new Date(d.day).toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' })}</td>
                          <td>{d.entree ? formatTime(d.entree) : '—'}</td>
                          <td>{d.sortie ? formatTime(d.sortie) : '—'}</td>
                          <td><strong>{d.minutesTravaillees > 0 ? formatMinutes(d.minutesTravaillees) : '—'}</strong></td>
                          <td><span className={`status-badge ${d.status}`}>
                            {d.status === 'present' ? '✅' : d.status === 'retard' ? '⚠ Retard' : '❌ Absent'}
                          </span></td>
                          <td>{d.delayMinutes > 0 ? `${d.delayMinutes} min` : '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {idx < Object.keys(reportByEmployee).length - 1 && <hr style={{ borderColor: '#e2e8f0', margin: '24px 0' }} />}
              </div>
            );
          })}
        </Modal.Body>
        <Modal.Footer>
          <button className="hr-btn hr-btn-secondary" onClick={() => setShowReport(false)}>Fermer</button>
        </Modal.Footer>
      </Modal>

      {/* Modal Import Excel Preview */}
      <Modal show={showImportModal} onHide={() => setShowImportModal(false)} size="xl" centered>
        <Modal.Header closeButton style={{ background: 'linear-gradient(135deg,#05cd99,#03a678)', color: 'white' }}>
          <Modal.Title>📊 Aperçu de l'Importation — {importData.length} employés</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: 28 }}>
          {importResult ? (
            <Alert variant={importResult.errors > 0 ? "warning" : "success"}>
              <h5>Importation terminée !</h5>
              <p>✅ Succès : {importResult.success} | ❌ Erreurs : {importResult.errors}</p>
              {importResult.details?.length > 0 && (
                <div style={{ maxHeight: '200px', overflowY: 'auto', fontSize: '0.85rem' }}>
                  <ul>
                    {importResult.details.map((d, i) => <li key={i}>{d.name} : {d.error}</li>)}
                  </ul>
                </div>
              )}
            </Alert>
          ) : (
            <>
              <p className="text-muted mb-3">Vérifiez les données avant de valider l'importation massive.</p>
              <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                <Table striped bordered hover size="sm">
                  <thead>
                    <tr>
                      <th>Nom</th>
                      <th>Email</th>
                      <th>Matricule</th>
                      <th>Rôle</th>
                      <th>Poste</th>
                    </tr>
                  </thead>
                  <tbody>
                    {importData.map((emp, i) => (
                      <tr key={i}>
                        <td>{emp.name}</td>
                        <td>{emp.email}</td>
                        <td>{emp.matricule}</td>
                        <td>{emp.role}</td>
                        <td>{emp.position}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <button className="hr-btn hr-btn-secondary" onClick={() => setShowImportModal(false)}>Fermer</button>
          {!importResult && (
            <button className="hr-btn hr-btn-success" onClick={saveImport} disabled={importLoading}>
              {importLoading ? '⏳ Importation...' : '💾 Valider l\'Importation'}
            </button>
          )}
        </Modal.Footer>
      </Modal>
    </div>
  );
}
